import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import { Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback if API empty
const FALLBACK = [
  { id: 'f1', title: '人可以窮，但不能懶', industry_tags: ['職場'], video_type_tags: ['口播'] },
  { id: 'f2', title: '頂級女友長什麼樣子', industry_tags: ['情感'], video_type_tags: ['敘事'] },
  { id: 'f3', title: '火機要一蚊定兩蚊？', industry_tags: ['生活'], video_type_tags: ['街訪'] },
  { id: 'f4', title: '香港的房子貴嗎？', industry_tags: ['理財'], video_type_tags: ['街訪'] },
  { id: 'f5', title: '原來生命可以脆弱到你想像唔到', industry_tags: ['情感'], video_type_tags: ['口播'] },
  { id: 'f6', title: 'They have the exact same hook', industry_tags: ['分析'], video_type_tags: ['拆解'] },
];

const hashHue = (s = '') =>
  Array.from(s).reduce((a, c) => a + c.charCodeAt(0), 0) % 360;

const CaseCard = ({ c }) => {
  const hue = hashHue(c.id || c.title);
  return (
    <div
      className="flex-shrink-0 w-[220px] md:w-[260px] mx-3 group cursor-default"
      data-testid={`vhof-case-${c.id}`}
    >
      <div
        className="relative rounded-xl overflow-hidden border border-white/[0.06] group-hover:border-lime-500/40 transition-all duration-500"
        style={{
          aspectRatio: '9/16',
          background: `linear-gradient(135deg, hsl(${hue} 35% 18%) 0%, hsl(${(hue + 40) % 360} 45% 12%) 50%, #090A0C 100%)`,
        }}
      >
        {/* Grid texture */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        {/* Watermark initial */}
        <div
          aria-hidden
          className="absolute -top-4 -right-2 font-serif font-bold text-white/[0.08] leading-none pointer-events-none select-none"
          style={{ fontSize: '180px' }}
        >
          {(c.title || '?').charAt(0)}
        </div>

        {/* Platform tag */}
        {c.platform_tags?.[0] && (
          <span className="absolute top-3 right-3 label-micro px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white border border-white/10">
            {c.platform_tags[0]}
          </span>
        )}

        {/* Play ring */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full border border-white/30 bg-black/30 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 group-hover:border-lime-500 group-hover:bg-lime-500/20 transition-all duration-500">
            <Play className="w-4 h-4 text-white group-hover:text-lime-500 ml-0.5" fill="currentColor" />
          </div>
        </div>

        {/* Bottom title overlay */}
        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
          <h4 className="font-serif font-semibold text-white text-sm md:text-base leading-tight line-clamp-2 drop-shadow">
            {c.title}
          </h4>
        </div>
      </div>

      {/* Meta under card */}
      <div className="mt-2.5 flex items-center gap-2 label-micro text-obsidian-400 min-h-[18px]">
        {c.industry_tags?.[0] && <span>{c.industry_tags[0]}</span>}
        {c.video_type_tags?.[0] && (
          <>
            <span className="text-obsidian-600">·</span>
            <span>{c.video_type_tags[0]}</span>
          </>
        )}
      </div>
    </div>
  );
};

const ViralHallOfFame = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [cases, setCases] = useState(FALLBACK);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/showcase/cases`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length >= 3) setCases(r.data);
      })
      .catch(() => {});
  }, []);

  // Duplicate list for seamless loop
  const track = [...cases, ...cases];

  return (
    <section
      ref={ref}
      id="hall-of-fame"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="viral-hall-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12 md:mb-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-lime-500/60" />
            <span className="label-micro text-lime-500">HALL · OF · FAME</span>
          </div>
          <h2 className="font-display font-semibold text-white tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            已被拆解的<span className="text-lime-500">爆款案例</span>
          </h2>
          <p className="text-obsidian-200 text-base md:text-lg mt-5">
            每一支都是平台已解剖過結構的百萬觀看 Reels — 你也可以在登入後學習。
          </p>
        </motion.div>
      </div>

      {/* Marquee track (infinite horizontal scroll) */}
      <div className="relative">
        {/* Edge fade masks */}
        <div className="absolute inset-y-0 left-0 w-24 md:w-40 z-10 pointer-events-none bg-gradient-to-r from-obsidian-900 to-transparent" />
        <div className="absolute inset-y-0 right-0 w-24 md:w-40 z-10 pointer-events-none bg-gradient-to-l from-obsidian-900 to-transparent" />

        <div
          className="flex marquee-track"
          style={{
            width: 'max-content',
            animation: `marquee-scroll ${Math.max(track.length * 4, 40)}s linear infinite`,
          }}
        >
          {track.map((c, i) => (
            <CaseCard key={`${c.id}-${i}`} c={c} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes marquee-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

export default ViralHallOfFame;
